import type { EventSettings } from '../services/events';
import { dropIndex } from './sortableDrag';

export const DEFAULT_RANK_SIZE = 5;

export interface RankingOrderEntry {
  rankings: string[];
  fullOrder?: string[];
}

export function rankSizeOf(settings?: Partial<EventSettings>): number {
  const rankSize = settings?.rankSize ?? settings?.rankingMaxSongs;
  return typeof rankSize === 'number' && rankSize > 0 ? rankSize : DEFAULT_RANK_SIZE;
}

export function effectiveBallot(
  entry: RankingOrderEntry,
  queuedIds: Set<string>,
  rankSize: number,
  excluded?: Set<string>,
): string[] {
  return (entry.fullOrder ?? entry.rankings).filter(id => queuedIds.has(id) && !excluded?.has(id)).slice(0, rankSize);
}

export function partitionDownvoted(order: string[], downvoted: Set<string>): string[] {
  return [...order.filter(id => !downvoted.has(id)), ...order.filter(id => downvoted.has(id))];
}

export function isPartitioned(order: string[], downvoted: Set<string>): boolean {
  const firstDownvoted = order.findIndex(id => downvoted.has(id));
  return firstDownvoted < 0 || order.slice(firstDownvoted).every(id => downvoted.has(id));
}

export function moveRespectsDownvoteBlock(order: string[], id: string, targetIndex: number, downvoted: Set<string>): boolean {
  return isPartitioned(moveToIndex(order, id, targetIndex), downvoted);
}

export function moveToDownvotedBlockEnd(order: string[], id: string): string[] {
  return moveToIndex(order, id, order.length);
}

export function moveAboveDownvotedBlock(order: string[], id: string, downvoted: Set<string>): string[] {
  const others = order.filter(existing => existing !== id);
  const firstDownvoted = others.findIndex(existing => downvoted.has(existing));
  return moveToIndex(order, id, firstDownvoted < 0 ? others.length : firstDownvoted);
}

export function moveToIndex(order: string[], id: string, targetIndex: number): string[] {
  const others = order.filter(existing => existing !== id);
  const index = Math.min(Math.max(Math.floor(targetIndex), 0), others.length);
  return [...others.slice(0, index), id, ...others.slice(index)];
}

export function addToRankingBottom(order: string[], id: string, rankSize: number): string[] | 'full' {
  const others = order.filter(existing => existing !== id);
  if (others.length >= rankSize) return 'full';
  return moveToIndex(order, id, others.length);
}

export function replaceInRanking(order: string[], incomingId: string, outgoingId: string, rankSize: number): string[] {
  const withoutIncoming = order.filter(existing => existing !== incomingId);
  const outgoingIndex = withoutIncoming.indexOf(outgoingId);
  if (outgoingIndex < 0) return order;
  const swapped = [...withoutIncoming];
  swapped[outgoingIndex] = incomingId;
  return moveToIndex(swapped, outgoingId, rankSize);
}

export function unrankTo(order: string[], id: string, rankSize: number, where: 'top' | 'bottom'): string[] {
  return moveToIndex(order, id, where === 'top' ? rankSize : order.length);
}

export function resolveDivider(fullOrder: string[], anchorId: string | null | undefined, keepIds: Set<string>): string | null {
  if (!anchorId) return null;
  const anchorIndex = fullOrder.indexOf(anchorId);
  if (anchorIndex < 0) return null;
  for (let index = anchorIndex; index >= 0; index--) {
    if (keepIds.has(fullOrder[index])) return fullOrder[index];
  }
  return null;
}

export function anchorBeforeMove(order: string[], anchorId: string | null, movingId: string): string | null {
  if (!anchorId || anchorId !== movingId) return anchorId;
  const index = order.indexOf(anchorId);
  return index > 0 ? order[index - 1] : null;
}

export function dropIntoOrder(order: string[], displayIds: string[], activeId: string, overId: string): string[] | null {
  const index = dropIndex(displayIds, activeId, overId);
  if (index === null || !order.includes(activeId)) return null;
  const beforeId = displayIds.filter(id => id !== activeId)[index];
  if (beforeId === undefined) return moveToIndex(order, activeId, order.length);
  const targetIndex = order.filter(id => id !== activeId).indexOf(beforeId);
  if (targetIndex < 0) return null;
  return moveToIndex(order, activeId, targetIndex);
}
